import { parse } from "date-fns";
import {
  ApiCategory,
  ApiCompetition,
  ApiCompetitionDetail,
  ApiCreateCategoryPayload,
  ApiCreateCompetitionPayload,
  ApiUpdateCompetitionPayload,
  ApiVenueDetail,
} from "griptonite-api-client";
import { z } from "zod";

import urlSchema from "@/utils/urlSchema";

export const DetailsFormSchema = z
  .object({
    venue: z.string().uuid(),
    league: z.string().uuid().nullable(),
    name: z.string({ message: "Name required" }).min(1, {
      message: "Name required",
    }),
    description: z.string().nullable(),
    startTime: z.string({ message: "Start time required" }).datetime(),
    endTime: z.string({ message: "End time required" }).datetime(),
    climbType: z.enum(["BOULDER", "SPORT"]),
    tvDedicatedMode: z.boolean(),
    tagEntry: z.boolean(),
    tagId: z.string().nullable(),
    media: z.array(
      z.object({
        uuid: z.string(),
      }),
    ),
    websiteUrl: urlSchema.nullable(),
    facebookUrl: urlSchema.nullable(),
    instagramUrl: urlSchema.nullable(),
    sponsorItems: z.array(z.string().uuid()),
  })
  .superRefine((obj, ctx) => {
    if (new Date(obj.startTime).getTime() >= new Date(obj.endTime).getTime()) {
      ctx.addIssue({
        message: "End time must be after start time",
        code: z.ZodIssueCode.custom,
        path: ["endTime"],
      });
    }
  });

export type DetailsFormValues = z.infer<typeof DetailsFormSchema>;

export const urlsArrayToFields = (urls: string[]) => {
  const fields = {
    websiteUrl: null,
    facebookUrl: null,
    instagramUrl: null,
  } as {
    websiteUrl: string | null;
    facebookUrl: string | null;
    instagramUrl: string | null;
  };

  urls.forEach((url) => {
    if (url.includes("facebook.com/")) {
      fields.facebookUrl = url;
    } else if (url.includes("instagram.com/")) {
      fields.instagramUrl = url;
    } else {
      fields.websiteUrl = url;
    }
  });

  return fields;
};

export const competitionToDetailsFormValues = (
  competition?: ApiCompetition | ApiCompetitionDetail,
  venue?: ApiVenueDetail,
  startDate?: string,
  leagueUuid?: string,
): Omit<DetailsFormValues, "startTime" | "endTime"> & {
  startTime: string | null;
  endTime: string | null;
} => {
  const startTimeFromStartDate = startDate
    ? parse(startDate, "yyyy-MM-dd", new Date()).toISOString()
    : undefined;
  return {
    venue: competition?.venues[0].uuid || venue?.uuid || "",
    name: competition?.name || "",
    description: competition?.description || null,
    startTime: competition?.startTime || startTimeFromStartDate || null,
    endTime: competition?.endTime || null,
    climbType: competition?.climbType || venue?.climbTypes[0] || "BOULDER",
    tvDedicatedMode:
      (competition && "tvDedicatedMode" in competition && competition?.tvDedicatedMode) || false,
    tagEntry: competition?.tagEntry || false,
    tagId: competition?.tags[0]?.id || null,
    media: competition?.media || [],
    league: competition?.league?.uuid || leagueUuid || null,
    sponsorItems: competition?.sponsorItems ? competition.sponsorItems.map((s) => s.uuid) : [],
    ...urlsArrayToFields(competition?.urls || []),
  };
};

export const detailsFormValuesToPayload = (
  values: DetailsFormValues,
): ApiCreateCompetitionPayload => {
  return {
    name: values.name,
    description: values.description,
    startTime: values.startTime,
    endTime: values.endTime,
    climbType: values.climbType,
    tvDedicatedMode: values.tvDedicatedMode,
    tagEntry: values.tagEntry,
    tags: values.tagId ? [{ id: values.tagId, venue: values.venue }] : [],
    media: values.media.map((m) => m.uuid),
    venues: [values.venue],
    ...(values.media.length ? { coverImage: values.media[0].uuid } : {}),
    urls: [values.websiteUrl, values.facebookUrl, values.instagramUrl].filter(
      (url) => url,
    ) as string[],
    league: values.league,
    sponsorItems: values.sponsorItems,
  };
};

export const CategoryFormSchema = z.object({
  name: z
    .string({ message: "Name required" })
    .min(1, {
      message: "Name required",
    })
    .trim(),
  minAge: z.number().int().min(0, "Must be positive").max(120, "Max age 120").nullable(),
  maxAge: z.number().int().min(0, "Must be positive").max(120, "Max age 120").nullable(),
  gender: z.enum(["MALE", "FEMALE"]).nullable(),
  abilityCategories: z
    .array(z.enum(["BEGINNER", "NOVICE", "INTERMEDIATE", "ADVANCED", "PROFESSIONAL", "ELITE"]))
    .nullable(),
  color: z.string().nullable(),
  sportType: z.enum(["LEAD", "TOPROPE"]).nullable(),
});

export type CategoryFormValues = z.infer<typeof CategoryFormSchema>;

export const categoryToCategoryFormValues = (category?: ApiCategory): CategoryFormValues => {
  return {
    name: category?.name || "",
    minAge: category?.minAge ?? null,
    maxAge: category?.maxAge ?? null,
    gender: category?.gender ?? null,
    abilityCategories: category?.abilityCategories ?? [
      "BEGINNER",
      "NOVICE",
      "INTERMEDIATE",
      "ADVANCED",
      "PROFESSIONAL",
      "ELITE",
    ],
    color: category?.color ?? null,
    sportType: category?.sportType ?? null,
  };
};

export const categoryFormValuesToPayload = (
  values: CategoryFormValues,
  venueUuid: string,
): ApiCreateCategoryPayload => {
  return {
    ...values,
    color: values.color ?? undefined,
    venues: [venueUuid],
    abilityCategories: values.abilityCategories?.length === 6 ? null : values.abilityCategories,
  };
};

// A cleared number input holds null, which zod would otherwise report as
// "Expected number, received null".
const requiredNumber = () => z.number({ message: "Required" });

export const ScoringFormSchema = z.object({
  liveLeaderboard: z.boolean().default(false),
  scoringSystem: z.enum([
    "ATTEMPTS",
    "TICKS_ONLY",
    "DYNAMIC",
    "CIRCUIT_NUMBER",
    "IFSC_RANKING",
    "IFSC_SPORT",
    "IFSC_2025",
    "OLYMPICS_2024",
    "CUSTOM",
  ]),
  attemptsSettings: z.object({
    flash: requiredNumber().min(0),
    secondTry: requiredNumber().min(0),
    thirdTry: requiredNumber().min(0),
    fourPlusTries: requiredNumber().min(0),
    segment: z.number().nullable().optional(),
    segment2: z.number().nullable().optional(),
    useZones: z.boolean().nullable().optional(),
  }),
  ticksOnlySettings: z.object({
    tick: requiredNumber().min(0),
    flashBonus: requiredNumber().min(0),
    segment: z.number().nullable().optional(),
    segment2: z.number().nullable().optional(),
    useZones: z.boolean().nullable().optional(),
  }),
  dynamicSettings: z.object({
    basePoints: requiredNumber().min(0),
    flashBonus: z
      .number()
      .min(0)
      .nullish()
      .transform((v) => v ?? 0),
    sharePointsWithinCategory: z.boolean().default(false),
  }),
  circuitNumberSettings: z.object({
    maxRoutes: z.number().min(1).nullable(),
    flashBonus: z
      .number()
      .min(0)
      .nullish()
      .transform((v) => v ?? 0),
    pointsMultiplier: z
      .number()
      .min(0.001)
      .nullish()
      .transform((v) => v ?? 1),
  }),
  customSettings: z.intersection(
    z.object({
      maxRoutes: z.number().min(1).nullable(),
      failedAttemptPenalty: z.number().min(0).nullable(),
      flashBonus: z.number().min(0).nullish(),
      useFailedAttemptPenalty: z.boolean().nullable().optional(),
      rulesText: z.string().nullable(),
    }),
    z.discriminatedUnion("segmentsCount", [
      z.object({
        segmentsCount: z.literal(0),
        baseRoutePointsConfig: z.object({
          successPoints: z.array(requiredNumber().min(0)),
        }),
      }),
      z.object({
        segmentsCount: z.literal(1),
        baseRoutePointsConfig: z.object({
          successPoints: z.array(requiredNumber().min(0)),
          segment1Points: z.array(requiredNumber().min(0)),
        }),
      }),
      z.object({
        segmentsCount: z.literal(2),
        baseRoutePointsConfig: z.object({
          successPoints: z.array(requiredNumber().min(0)),
          segment1Points: z.array(requiredNumber().min(0)),
          segment2Points: z.array(requiredNumber().min(0)),
        }),
      }),
    ]),
  ),
});

const scoringSettingsDefaults = {
  attemptsSettings: {
    flash: 10,
    secondTry: 7,
    thirdTry: 4,
    fourPlusTries: 1,
    segment: null,
    segment2: null,
  },
  ticksOnlySettings: {
    tick: 10,
    flashBonus: 5,
    segment: null,
    segment2: null,
  },
  dynamicSettings: {
    basePoints: 10,
    flashBonus: 0,
    sharePointsWithinCategory: false,
  },
  circuitNumberSettings: {
    maxRoutes: null,
    flashBonus: 5,
    pointsMultiplier: 1,
  },
  customSettings: {
    segmentsCount: 0,
    baseRoutePointsConfig: {
      successPoints: [10, 5],
    },
    failedAttemptPenalty: null,
    flashBonus: null,
    maxRoutes: null,
    rulesText: null,
  },
};

export type ScoringFormValues = z.infer<typeof ScoringFormSchema>;

export const competitionToScoringFormValues = (
  competition?: ApiCompetition | ApiCompetitionDetail,
): ScoringFormValues => {
  const result: Partial<ScoringFormValues> = {
    liveLeaderboard: competition?.liveLeaderboard || false,
  };
  if (!competition?.scoringSystem) {
    return {
      scoringSystem: "ATTEMPTS",
      ...scoringSettingsDefaults,
      ...result,
    } as ScoringFormValues;
  }
  result.scoringSystem = competition.scoringSystem;
  Object.assign(result, scoringSettingsDefaults);
  switch (competition.scoringSystem) {
    case "ATTEMPTS":
      result.attemptsSettings =
        competition.scoringSystemSettings as ScoringFormValues["attemptsSettings"];
      result.attemptsSettings.useZones = !!competition.scoringSystemSettings.segment;
      break;
    case "TICKS_ONLY":
      result.ticksOnlySettings =
        competition.scoringSystemSettings as ScoringFormValues["ticksOnlySettings"];
      result.ticksOnlySettings.useZones = !!competition.scoringSystemSettings.segment;
      break;
    case "DYNAMIC":
      result.dynamicSettings =
        competition.scoringSystemSettings as ScoringFormValues["dynamicSettings"];
      break;
    case "CIRCUIT_NUMBER":
      result.circuitNumberSettings =
        competition.scoringSystemSettings as ScoringFormValues["circuitNumberSettings"];
      break;
    case "CUSTOM":
      result.customSettings =
        competition.scoringSystemSettings as ScoringFormValues["customSettings"];
      result.customSettings.useFailedAttemptPenalty =
        !!competition.scoringSystemSettings.failedAttemptPenalty;
      result.customSettings.rulesText =
        ("settings" in competition && competition.settings.customScoringExplanationText) || null;
      break;
  }
  return result as ScoringFormValues;
};

export const scoringFormValuesToPayload = (
  values: ScoringFormValues,
): ApiUpdateCompetitionPayload => {
  let scoringSystemSettings: ApiCompetitionDetail["scoringSystemSettings"];
  const settings: Partial<ApiCompetitionDetail["settings"]> = {};

  switch (values.scoringSystem) {
    case "ATTEMPTS":
      scoringSystemSettings = values.attemptsSettings;
      break;
    case "TICKS_ONLY":
      scoringSystemSettings = values.ticksOnlySettings;
      break;
    case "DYNAMIC":
      scoringSystemSettings = values.dynamicSettings;
      break;
    case "CIRCUIT_NUMBER":
      scoringSystemSettings = values.circuitNumberSettings;
      break;
    case "CUSTOM":
      scoringSystemSettings = values.customSettings;
      settings.customScoringExplanationText = values.customSettings.rulesText ?? null;
      break;
    default:
      scoringSystemSettings = {};
  }

  return {
    liveLeaderboard: values.liveLeaderboard,
    scoringSystem: values.scoringSystem,
    scoringSystemSettings,
    settings,
  };
};
